<template>
  <div role="tabpanel" class="tab-pane active col-sm-12" id="members" v-if="model">
    <div class="col-md-8 no-left-padding">
      <h3>Members</h3>
      <table class="table">
        <thead>
          <tr>
            <th scope="col" class="col-md-10">Filter by</th>
            <th scope="col" class="col-md-2">members</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <a
                href="javascript:void(0)"
                @click="toggleFilter('all')"
                data-test="all-members-filter"
              >
                All Members</a
              >
            </td>
            <td data-test="all-members-length">{{ model.members.length }}</td>
          </tr>
          <tr>
            <td>
              <a
                href="javascript:void(0)"
                @click="toggleFilter('solo')"
                data-test="has-solo-filter"
              >
                Members with completed Solos</a
              >
            </td>
            <td data-test="has-solo-length">{{ peopleWithSoloReports.length }}</td>
          </tr>
          <tr>
            <td>
              <a
                href="javascript:void(0)"
                @click="toggleFilter('viewpoints')"
                data-test="viewpoints-filter"
              >
                Members with 2+ Viewpoints</a
              >
            </td>
            <td data-test="viewpoints-length">{{ peopleWithTwoViewpoints.length }}</td>
          </tr>
          <tr>
            <td>
              <a
                href="javascript:void(0)"
                @click="toggleFilter('setup_team')"
                data-test="setup-team-filter"
              >
                Members who have set up a Team</a
              >
            </td>
            <td data-test="setup-team-length">{{ peopleSetUpATeam.length }}</td>
          </tr>
          <tr>
            <td>
              <a href="javascript:void(0)" @click="toggleFilter('teq')" data-test="teqs-filter">
                Members with TEQs Completed</a
              >
            </td>
            <td data-test="teqs-length">{{ peopleWithTEQs.length }}</td>
          </tr>
          <tr>
            <td>
              <a
                href="javascript:void(0)"
                @click="toggleFilter('plans')"
                data-test="develop-filter"
              >
                Members with Develop Plans</a
              >
            </td>
            <td data-test="develop-length">{{ peopleWithDevelopPlans.length }}</td>
          </tr>
          <tr>
            <td>
              <a
                href="javascript:void(0)"
                @click="toggleFilter('pathways')"
                data-test="pathways-filter"
              >
                Members with Team Pathway Progress</a
              >
            </td>
            <td data-test="pathways-length">{{ peopleWithPathways.length }}</td>
          </tr>
          <tr>
            <td>
              <a
                href="javascript:void(0)"
                @click="toggleFilter('personal-pathways')"
                data-test="personal-pathways-filter"
              >
                Members with Personal Pathway Progress</a
              >
            </td>
            <td data-test="personal-pathways-length">{{ peopleWithPersonalPathways.length }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <OrganisationDetails
      v-if="appliedFilter"
      :model="model"
      :sortedMembers="sortedMembers"
      :appliedFilter="appliedFilter"
      @revalidate-org-data="revalidateOrgData"
    ></OrganisationDetails>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import OrganisationDetails from "./accredited-user-area-organisation-details.vue";
import { OrganisationAndMembersApiModel, OrganisationMemberApiModel } from "../../../api-models";

const props = defineProps<{
  model: OrganisationAndMembersApiModel;
}>();

const emit = defineEmits(["revalidate-org-data"]);

const appliedFilter = ref<string | null>(null);

const toggleFilter = (filter: string): void => {
  appliedFilter.value = appliedFilter.value === filter ? null : filter;
};

const revalidateOrgData = (): void => {
  emit("revalidate-org-data");
};

const guestMembers = computed((): OrganisationMemberApiModel[] => {
  return props.model?.members.filter((x) => x.subscriptionLevel === "Guest") ?? [];
});

const peopleWithSoloReports = computed((): OrganisationMemberApiModel[] => {
  return props.model?.members.filter((x) => x.solo) ?? [];
});

const peopleWithTwoViewpoints = computed((): OrganisationMemberApiModel[] => {
  return props.model?.members.filter((x) => x.viewpoints?.length > 1) ?? [];
});

const peopleWithDevelopPlans = computed((): OrganisationMemberApiModel[] => {
  return (
    props.model?.members.filter(
      (x) =>
        (x.development && x.development.currentStrengthProgress?.strengthItemId) ||
        x.development.otherProgress?.some((progress) => progress),
    ) ?? []
  );
});

const peopleSetUpATeam = computed((): OrganisationMemberApiModel[] => {
  return props.model?.members.filter((x) => x.hasSetUpTeam) ?? [];
});

const peopleWithTEQs = computed((): OrganisationMemberApiModel[] => {
  return props.model?.members.filter((x) => x.teqsCompleted.length > 0) ?? [];
});

const peopleWithPathways = computed((): OrganisationMemberApiModel[] => {
  return props.model?.members.filter((x) => {
    return (
      (x.pathways.currentPathwaysInProgress?.length > 0 || x.pathways.otherProgress?.length > 0) ??
      []
    );
  });
});

const peopleWithPersonalPathways = computed((): OrganisationMemberApiModel[] => {
  return props.model?.members.filter((x) => {
    return (
      (x.personalPathways?.currentPathwaysInProgress?.length > 0 || x.personalPathways?.otherProgress?.length > 0) ??
      []
    );
  });
});

const filteredMembers = computed((): OrganisationMemberApiModel[] => {
  if (!appliedFilter.value || appliedFilter.value === "all") return props.model?.members ?? [];
  if (appliedFilter.value === "guests") return guestMembers.value;
  if (appliedFilter.value === "solo") return peopleWithSoloReports.value;
  if (appliedFilter.value === "viewpoints") return peopleWithTwoViewpoints.value;
  if (appliedFilter.value === "setup_team") return peopleSetUpATeam.value;
  if (appliedFilter.value === "teq") return peopleWithTEQs.value;
  if (appliedFilter.value === "plans") return peopleWithDevelopPlans.value;
  if (appliedFilter.value === "pathways") return peopleWithPathways.value;
  if (appliedFilter.value === "personal-pathways") return peopleWithPersonalPathways.value;
  return [];
});

const sortedMembers = computed((): OrganisationMemberApiModel[] => {
  return [...filteredMembers.value].sort((a, b) => {
    // Admins come before non-admins
    if (a.role === "Admin" && b.role !== "Admin") {
      return -1;
    }
    return 1;
  });
});
</script>

<style scoped lang="less">
.no-left-padding {
  padding-left: 0;
}
</style>
